import type { CSSProperties } from 'react';

/**
 * Layout constants lifted from the design-of-record demos.
 *
 * The demos are inline-styled, and their scale (13.5px type, 9px radii, 650 weights) does
 * not exist in Tailwind's default scale — reproducing it through utilities would mean
 * redesigning it. So the measurements are carried verbatim here and shared, and every
 * COLOUR still resolves through a token (`var(--…)`) or a token-built class. There is not
 * one colour literal in this file.
 */

export const cardStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  overflow: 'hidden',
};

export const cardHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '13px 18px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--surface-2)',
  fontSize: 13.5,
  fontWeight: 650,
};

export const tileStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '16px 18px',
};

export const tileLabelStyle: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.07em',
  textTransform: 'uppercase',
};

export const eyebrowStyle: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.07em',
  textTransform: 'uppercase',
};

export const monoStyle: CSSProperties = {
  fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
};

/**
 * Was `minHeight: 'calc(100vh - 89px)'` — a magic-number attempt to LOOK full height with
 * short content, which never gave long content anywhere of its own to go: it only set a
 * floor, never a ceiling, so a long list still pushed the whole document taller. Replaced
 * with the real mechanism (AdminShell layout fix): `masterDetailStyle` now stretches this
 * pane to fill its grid row exactly, and `overflow-y: auto` here is what actually contains
 * an overflowing list — scrolling on its own, never the rail, never the detail pane, never
 * the document. `minHeight: 0` is required alongside `overflow-y: auto` in a stretched grid
 * item for the same reason it is in a flex child: without it, the pane refuses to shrink
 * below its content's natural height and the overflow escapes anyway.
 */
export const listPaneStyle: CSSProperties = {
  borderRight: '1px solid var(--border)',
  background: 'var(--surface)',
  minHeight: 0,
  overflowY: 'auto',
};

export const listPaneHeaderStyle: CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

/**
 * `height: '100%'` fills exactly the scroll region AdminShell now gives it — never taller,
 * never shorter — so this grid never itself overflows its parent; any overflow is absorbed
 * by `listPaneStyle` / `detailPaneStyle` below, each independently.
 *
 * `alignItems: 'stretch'` — flipped from `'start'`. `'start'` let each column size to its
 * OWN content (which is exactly why the two columns used to sit at different heights and
 * why nothing here could establish a scroll boundary for either). Stretch is what makes
 * both columns exactly this grid's height, which is what `overflow-y: auto` on each of them
 * needs to have anything to be bounded BY.
 */
export const masterDetailStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '288px minmax(0,1fr)',
  alignItems: 'stretch',
  height: '100%',
};

/**
 * `overflow-y: auto` + `minHeight: 0` — this pane's own independent scroll, so a tall
 * application/user/grant detail no longer drags the shared list pane (or the rail, or the
 * document) out of view with it; D23 stays untouched, no `max-width` here or anywhere in
 * this file — this is scroll containment only, content still fills the pane at every width.
 *
 * This is also Audit's OWN outer wrapper (it renders `detailPaneStyle` directly, with no
 * `masterDetailStyle` around it) — there, `masterDetailStyle`'s `height: 100%` never applies
 * (nothing stretches it), so it stays its natural auto height and these two properties are a
 * harmless no-op: nothing to scroll inside a box that is already exactly as tall as its
 * content. Audit's actual scroll comes from AdminShell's own fallback region, unaffected by
 * this file at all — see the comment there.
 */
export const detailPaneStyle: CSSProperties = {
  padding: '32px 40px 110px',
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
};

/**
 * The detail column fills its pane at every width — no cap.
 *
 * The design export carries `max-width: 1120px` here and it is live at render time, so the
 * exported demo shows an empty band to the right of the cards on a wide window. That cap is
 * an artefact of the export rather than the intent: ruled by the operator (D23), the content
 * region fills the viewport on every screen, at any width. Do not reintroduce a cap here
 * from a future export without reopening that ruling.
 */
export const detailColumnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 22,
  width: '100%',
};

/**
 * The scrolling row list's container — the `<ul>` that holds `listRowStyle` rows on the
 * Users, Grants and Applications screens.
 *
 * The 12px padding is not arbitrary and must stay in step with the list column's other
 * controls: the search field and the segmented control above the list sit in a wrapper
 * padded 12px, and a row is `width: 100%` of THIS element's content box. When this padded
 * 8px, every row rendered 4px further left and 4px further right than the controls above
 * it — 8px wider — so a selected or hovered row's border visibly broke the column's
 * vertical line. The rows never overflowed the container; they were flush with a content
 * box the eye had no other reason to believe in. One value, three screens: changing it
 * here moves all of them, and per-screen padding is how the two drift apart again.
 */
export const listContainerStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 12,
};

export const listRowStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  padding: '10px 11px',
  borderRadius: 9,
  cursor: 'pointer',
  textAlign: 'left',
};

export const searchFieldStyle: CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 8,
  fontSize: 12.5,
  boxSizing: 'border-box',
};

export const textFieldStyle: CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 8,
  fontSize: 13.5,
  boxSizing: 'border-box',
};

export const buttonStyle: CSSProperties = {
  padding: '8px 13px',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 650,
  cursor: 'pointer',
};

export const primaryButtonStyle: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 650,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const modalButtonStyle: CSSProperties = {
  padding: '9px 15px',
  borderRadius: 9,
  fontSize: 13,
  fontWeight: 650,
  cursor: 'pointer',
};

export const quietButtonStyle: CSSProperties = {
  padding: '5px 10px',
  borderRadius: 7,
  fontSize: 12.5,
  fontWeight: 650,
  cursor: 'pointer',
};

export const rowActionStyle: CSSProperties = {
  padding: '5px 9px',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

export const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 650,
  marginBottom: 6,
};

export const hintStyle: CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  marginTop: 5,
  lineHeight: 1.5,
};

export const checkboxRowStyle: CSSProperties = {
  display: 'flex',
  gap: 11,
  alignItems: 'flex-start',
  padding: '12px 13px',
  borderRadius: 9,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  cursor: 'pointer',
};

export const checkboxStyle: CSSProperties = {
  width: 16,
  height: 16,
  margin: '2px 0 0',
  accentColor: 'var(--accent)',
  flex: 'none',
};

export const grantRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '14px 18px',
  borderBottom: '1px solid var(--border)',
};

export const detailNameStyle: CSSProperties = {
  fontSize: 19,
  fontWeight: 650,
  letterSpacing: '-.015em',
};
